import { SPORT_ID, getDefaultDemoPayload } from '@/config/sports'

import { isDemo } from './matchConfigService'

import type { JsonMatchData } from './jsonDataService'

const S3_BUCKET_URL = import.meta.env.VITE_S3_BUCKET

// Default demo payload path - can be overridden for multi-scenario support
let currentDemoPayload = getDefaultDemoPayload(SPORT_ID.VOLLEYBALL) || ''

/**
 * Set the current demo payload path for multi-scenario support
 */
export const setDemoPayload = (payloadPath: string) => {
  currentDemoPayload = payloadPath
}

/**
 * Set the demo sport - updates the default payload to match
 */
export const setDemoSport = (sportId: SPORT_ID) => {
  const defaultPayload = getDefaultDemoPayload(sportId)
  if (defaultPayload) {
    currentDemoPayload = defaultPayload
  }
}

/**
 * Get the current demo payload path
 */
export const getDemoPayload = (): string => {
  return currentDemoPayload
}

export const fetchMatchScore = async (customerId: string, eventGuid: string): Promise<JsonMatchData> => {
  try {
    if (!customerId || !eventGuid) {
      throw new Error('customerId and eventGuid are required to fetch match score')
    }

    // Demo mode: fetch from local demo payload
    if (isDemo(customerId)) {
      const response = await fetch(currentDemoPayload)
      if (!response.ok) {
        throw new Error(`Failed to fetch demo payload: ${response.status}`)
      }
      const data = await response.json()
      // Demo payloads ship with static periodStartedAt timestamps that may sit
      // in the past or future relative to the viewer's clock. To produce a
      // tickling, sensible-looking clock during demo viewing, rebase any
      // RUNNING-phase periodStartedAt to (now - _demoLiveOffsetSeconds), where
      // _demoLiveOffsetSeconds is an optional hint baked into the payload
      // (defaults to 0 so the clock starts at the period baseline and ticks
      // up). Strip the hint before returning so it never leaks into bug logic.
      const eventState = data?.eventState as
        | { phase?: string; periodStartedAt?: string | null; _demoLiveOffsetSeconds?: number }
        | undefined
      if (eventState?.phase === 'RUNNING') {
        const offsetSeconds = Number(eventState._demoLiveOffsetSeconds ?? 0)
        eventState.periodStartedAt = new Date(Date.now() - offsetSeconds * 1000).toISOString()
        delete eventState._demoLiveOffsetSeconds
      }
      return data
    }

    // Production mode: fetch from S3
    if (!S3_BUCKET_URL) {
      throw new Error('VITE_S3_BUCKET environment variable not configured')
    }

    // Add timestamp query parameter to invalidate cache
    const timestamp = new Date().getTime()
    const url = `${S3_BUCKET_URL}/${customerId}.${eventGuid}.json?t=${timestamp}`
    const response = await fetch(url)

    if (!response.ok) {
      throw new Error(`Failed to fetch match score: ${response.status} ${response.statusText}`)
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error fetching match score:', error)
    throw error
  }
}
